import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import {
  Users,
  Plus,
  Search,
  Filter,
  Edit2,
  Trash2,
  Shield,
  ShieldOff,
  CheckCircle,
  XCircle,
  Key,
  Loader,
  UserPlus,
  Building2,
  UserCheck,
  UserX,
} from 'lucide-react';
import { adminAPI } from '../services/api';
import axios from 'axios';
import type { User, Organization, PaginatedResponse } from '../types';
import { theme } from '../styles/theme';
import { MetricTile, MetricTileGrid } from '../components/MetricTile';
import { extractApiError } from '../utils/errorMessages';
import { useToast } from '../hooks/use-toast';
import { useConfirm } from '../contexts/ConfirmContext';

interface UserListParams {
  search?: string;
  status?: string;
  is_admin?: string;
}

const AdminUsers: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const confirm = useConfirm();

  // Redirect non-admin users
  if (!user?.is_superuser) {
    return <Navigate to="/dashboard" replace />;
  }

  // State
  const [users, setUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [adminFilter, setAdminFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  // Stats
  const [stats, setStats] = useState({
    total_users: 0,
    active_users: 0,
    inactive_users: 0,
    admin_users: 0,
  });

  // Form state for creating user
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    is_superuser: false,
    organization: '',
  });

  // Password reset state
  const [newPassword, setNewPassword] = useState('');

  // DigiPART assignment state
  const [showDigipartModal, setShowDigipartModal] = useState(false);
  const [digipartUsername, setDigipartUsername] = useState('');
  const [digipartForce, setDigipartForce] = useState(false);
  const [digipartLoading, setDigipartLoading] = useState(false);
  const [digipartConflict, setDigipartConflict] = useState<string | null>(null);

  // Load users
  const loadUsers = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params: UserListParams = {};
      if (searchQuery) params.search = searchQuery;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (adminFilter !== 'all') params.is_admin = adminFilter;

      const response = await adminAPI.users.list(params);
      // Handle paginated response from DRF
      const userData = (response.data as unknown as PaginatedResponse<User>).results ?? (response.data as User[]);
      setUsers(Array.isArray(userData) ? userData : []);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast({ title: extractApiError(error, "Couldn't load users"), variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Load stats
  const loadStats = async () => {
    try {
      const response = await adminAPI.users.stats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  // Load organizations
  const loadOrganizations = async () => {
    try {
      const response = await adminAPI.organizations.list();
      const orgData = (response.data as unknown as PaginatedResponse<Organization>).results ?? (response.data as Organization[]);
      setOrganizations(Array.isArray(orgData) ? orgData : []);
    } catch (error) {
      console.error('Failed to load organizations:', error);
    }
  };

  useEffect(() => {
    loadUsers();
    loadStats();
    loadOrganizations();
  }, [searchQuery, statusFilter, adminFilter]);

  // Create user
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Creating user with data:', newUser);
    try {
      const response = await adminAPI.users.create(newUser);
      console.log('User created successfully:', response.data);
      toast({ title: `${newUser.username} added`, variant: 'success' });
      setShowCreateModal(false);
      setNewUser({
        username: '',
        email: '',
        full_name: '',
        password: '',
        is_superuser: false,
        organization: '',
      });
      loadUsers();
      loadStats();
    } catch (error: unknown) {
      console.error('Failed to create user:', error);
      let errorMsg = 'Failed to create user';
      if (axios.isAxiosError(error)) {
        const data = error.response?.data as Record<string, unknown> | undefined;
        errorMsg = (data?.username as string[] | undefined)?.[0]
          ?? (data?.email as string[] | undefined)?.[0]
          ?? (data?.detail as string | undefined)
          ?? (data ? JSON.stringify(data) : undefined)
          ?? error.message
          ?? 'Failed to create user';
      }
      toast({ title: errorMsg, variant: 'error' });
    }
  };

  // The prompt names the outcome rather than asking about "toggling a status",
  // so the button and the question agree about what is going to happen.
  const handleToggleActive = async (target: User) => {
    const deactivating = target.is_active;
    const ok = await confirm({
      title: deactivating ? `Deactivate ${target.username}?` : `Reactivate ${target.username}?`,
      description: deactivating ? 'They will not be able to sign in.' : undefined,
      confirmText: deactivating ? 'Deactivate' : 'Reactivate',
    });
    if (!ok) return;

    try {
      await adminAPI.users.toggleActive(String(target.id));
      loadUsers(true);
      loadStats();
    } catch (error: unknown) {
      toast({ title: extractApiError(error, "Couldn't change that user's access"), variant: 'error' });
    }
  };

  const handleToggleAdmin = async (target: User) => {
    const granting = !target.is_superuser;
    const ok = await confirm({
      title: granting ? `Make ${target.username} an admin?` : `Remove admin from ${target.username}?`,
      description: granting ? 'Admins can manage every user and organisation.' : undefined,
      confirmText: granting ? 'Make admin' : 'Remove admin',
    });
    if (!ok) return;

    try {
      await adminAPI.users.toggleAdmin(String(target.id));
      loadUsers(true);
      loadStats();
    } catch (error: unknown) {
      toast({ title: extractApiError(error, "Couldn't change admin rights"), variant: 'error' });
    }
  };

  const handleDeleteUser = async (userId: string, username: string) => {
    const ok = await confirm({
      title: `Delete ${username}?`,
      description: 'This cannot be undone.',
      confirmText: 'Delete',
      destructive: true,
    });
    if (!ok) return;

    try {
      await adminAPI.users.delete(userId);
      toast({ title: `${username} deleted`, variant: 'success' });
      loadUsers(true);
      loadStats();
    } catch (error: unknown) {
      toast({ title: extractApiError(error, "Couldn't delete that user"), variant: 'error' });
    }
  };

  // Reset password
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      await adminAPI.users.resetPassword(String(selectedUser.id), newPassword);
      toast({ title: `Password reset for ${selectedUser.username}`, variant: 'success' });
      setShowPasswordModal(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error: unknown) {
      toast({ title: extractApiError(error, "Couldn't reset that password"), variant: 'error' });
    }
  };

  // Open DigiPART assign modal
  const handleOpenDigipartModal = (targetUser: User) => {
    setSelectedUser(targetUser);
    setDigipartUsername('');
    setDigipartForce(false);
    setDigipartConflict(null);
    setShowDigipartModal(true);
  };

  // Assign DigiPART username
  const handleDigipartAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    setDigipartLoading(true);
    try {
      await adminAPI.users.assignDigipart(String(selectedUser.id), digipartUsername, digipartForce);
      setShowDigipartModal(false);
      setSelectedUser(null);
      loadUsers(true);
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        setDigipartConflict((error.response.data as Record<string, unknown>)?.existing_username as string ?? digipartUsername);
      } else {
        toast({ title: extractApiError(error, "Couldn't assign that DigiPART username"), variant: 'error' });
      }
    } finally {
      setDigipartLoading(false);
    }
  };

  const handleDigipartRevoke = async (targetUser: User) => {
    const ok = await confirm({
      title: `Revoke ${targetUser.digipart_username} from ${targetUser.username}?`,
      description: 'They will lose access to DigiPART until a username is assigned again.',
      confirmText: 'Revoke',
      destructive: true,
    });
    if (!ok) return;

    try {
      await adminAPI.users.revokeDigipart(String(targetUser.id));
      loadUsers(true);
    } catch (error: unknown) {
      toast({ title: extractApiError(error, "Couldn't revoke that DigiPART username"), variant: 'error' });
    }
  };

  // DigiPART status badge component
  const digipartStatusConfig: Record<string, { label: string; classes: string }> = {
    not_assigned: { label: 'Not Assigned', classes: 'bg-bg-subtle text-text-secondary' },
    assigned:     { label: 'Assigned',     classes: 'bg-interactive-primary/10 text-interactive-primary' },
    connected:    { label: 'Connected',    classes: 'bg-qc-success/10 text-qc-success' },
    session_expired: { label: 'Session Expired', classes: 'bg-qc-warning/10 text-qc-warning' },
    failed:       { label: 'Failed',       classes: 'bg-qc-error/10 text-qc-error' },
  };

  const DigipartStatusBadge = ({ status }: { status: User['digipart_connection_status'] }) => {
    const key = status ?? 'not_assigned';
    const config = digipartStatusConfig[key] ?? digipartStatusConfig['not_assigned'];
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.classes}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Back Button */}
      <Link
        to="/admin" 
        className={`inline-flex items-center ${theme.text.secondary} hover:text-interactive-primary-hover mb-4 transition`}
      >
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Admin Dashboard
      </Link>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="sr-only">User Management</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-6 py-3 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse font-semibold rounded-lg transition duration-200 flex items-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Create User</span>
        </button>
      </div>

      {/* Stats */}
      <MetricTileGrid columns={4} className="mb-6">
        <MetricTile label="Total Users" value={stats.total_users} IconComponent={Users} />
        <MetricTile label="Active" value={stats.active_users} IconComponent={CheckCircle} />
        <MetricTile label="Inactive" value={stats.inactive_users} IconComponent={XCircle} />
        <MetricTile label="Admins" value={stats.admin_users} IconComponent={Shield} />
</MetricTileGrid>

      {/* Filters */}
      <div className={`${theme.card.background} rounded-lg shadow p-6 mb-6`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-tertiary w-5 h-5" />
              <input
                type="text"
                placeholder="Search by username, email, or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-text-tertiary" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select
              value={adminFilter}
              onChange={(e) => setAdminFilter(e.target.value)}
              className={`px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
            >
              <option value="all">All Roles</option>
              <option value="true">Admins Only</option>
              <option value="false">Regular Users</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className={`${theme.card.background} rounded-lg shadow p-12 text-center`}>
          <Loader className="w-12 h-12 text-interactive-primary animate-spin mx-auto mb-4" />
          <p className={`${theme.text.secondary}`}>Loading users...</p>
        </div>
      ) : (
        <div className={`${theme.card.background} rounded-lg shadow overflow-hidden`}>
          <table className="w-full">
            <thead className="bg-bg-subtle">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  DigiPART
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${theme.border.primary.replace('border-', 'divide-')}`}>
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-bg-subtle">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-bg-subtle border border-border flex items-center justify-center text-text-primary font-semibold">
                        {u.username.charAt(0).toUpperCase()}
                      </div>
                      <div className="ml-4">
                        <div className={`text-sm font-medium ${theme.text.primary}`}>
                          {u.full_name}
                        </div>
                        <div className="text-sm text-text-tertiary">
                          @{u.username}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className={`px-6 py-4 text-sm ${theme.text.primary}`}>
                    {u.email}
                  </td>
                  <td className={`px-6 py-4 text-sm ${theme.text.primary}`}>
                    {u.organization_name ? (
                      <span className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-subtle px-2 py-0.5 text-xs font-medium text-text-secondary">
                        <Building2 className="w-3 h-3" aria-hidden="true" />
                        {u.organization_name}
                      </span>
                    ) : (
                      <span className="text-text-tertiary">No organisation</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {u.is_superuser ? (
                      <span className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-subtle px-2 py-0.5 text-xs font-medium text-text-secondary">
                        <Shield className="w-3 h-3" aria-hidden="true" />
                        Admin
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-subtle px-2 py-0.5 text-xs font-medium text-text-secondary">
                        User
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {u.is_active ? (
                      <span className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-subtle px-2 py-0.5 text-xs font-medium text-text-secondary">
                        <CheckCircle className="w-3 h-3" aria-hidden="true" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-xs font-medium text-text-tertiary">
                        <XCircle className="w-3 h-3" aria-hidden="true" />
                        Inactive
                      </span>
                    )}
                  </td>
                  {/* DigiPART status column */}
                  <td className="px-6 py-4" data-testid={`digipart-cell-${u.id}`}>
                    <DigipartStatusBadge status={u.digipart_connection_status} />
                    {u.digipart_username && (
                      <p className="text-xs text-text-tertiary mt-1 font-mono">
                        {u.digipart_username}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleToggleActive(u)}
                        className="p-2 text-text-secondary hover:text-qc-success"
                        title="Toggle Active Status"
                      >
                        {u.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleToggleAdmin(u)}
                        className="p-2 text-text-secondary hover:text-text-secondary"
                        title="Toggle Admin Status"
                        disabled={u.id === user.id}
                      >
                        {u.is_superuser ? <ShieldOff className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedUser(u);
                          setShowPasswordModal(true);
                        }}
                        className="p-2 text-text-secondary hover:text-interactive-primary-hover"
                        title="Reset Password"
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      {/* DigiPART assign / revoke */}
                      {(!u.digipart_connection_status || u.digipart_connection_status === 'not_assigned') ? (
                        <button
                          onClick={() => handleOpenDigipartModal(u)}
                          className="p-2 text-text-secondary hover:text-interactive-primary-hover"
                          title="Assign DigiPART Username"
                          data-testid={`digipart-assign-${u.id}`}
                        >
                          <UserCheck className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleDigipartRevoke(u)}
                          className="p-2 text-text-secondary hover:text-qc-error"
                          title="Revoke DigiPART Username"
                          data-testid={`digipart-revoke-${u.id}`}
                        >
                          <UserX className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteUser(String(u.id), u.username)}
                        className="p-2 text-text-secondary hover:text-qc-error"
                        title="Delete User"
                        disabled={u.id === user.id}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-text-primary bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${theme.card.background} rounded-lg p-8 max-w-md w-full mx-4`}>
            <h2 className={`text-2xl font-bold ${theme.text.primary} mb-6 flex items-center`}>
              <UserPlus className="w-6 h-6 mr-2" />
              Create New User
            </h2>
            <form onSubmit={handleCreateUser}>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                    Username *
                  </label>
                  <input
                    type="text"
                    required
                    value={newUser.username}
                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                    className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                    className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                    Password *
                  </label>
                  <input
                    type="password"
                    required
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                    Organization
                  </label>
                  <select
                    value={newUser.organization}
                    onChange={(e) => setNewUser({ ...newUser, organization: e.target.value })}
                    className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                  >
                    <option value="">No Organization</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_superuser"
                    checked={newUser.is_superuser}
                    onChange={(e) => setNewUser({ ...newUser, is_superuser: e.target.checked })}
                    className="w-4 h-4 text-interactive-primary border-border rounded focus:ring-interactive-primary"
                  />
                  <label htmlFor="is_superuser" className={`ml-2 text-sm ${theme.text.secondary}`}>
                    Make this user an administrator
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className={`px-4 py-2 ${theme.text.secondary} hover:bg-bg-subtle rounded-lg`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse rounded-lg"
                >
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-text-primary bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${theme.card.background} rounded-lg p-8 max-w-md w-full mx-4`}>
            <h2 className={`text-2xl font-bold ${theme.text.primary} mb-6 flex items-center`}>
              <Key className="w-6 h-6 mr-2" />
              Reset Password
            </h2>
            <p className={`${theme.text.secondary} mb-4`}>
              Reset password for user: <strong>{selectedUser.username}</strong>
            </p>
            <form onSubmit={handleResetPassword}>
              <div className="mb-6">
                <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                  New Password *
                </label>
                <input
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                  placeholder="Enter new password"
                />
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setNewPassword('');
                    setSelectedUser(null);
                  }}
                  className={`px-4 py-2 ${theme.text.secondary} hover:bg-bg-subtle rounded-lg`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse rounded-lg"
                >
                  Reset Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DigiPART Assignment Modal */}
      {showDigipartModal && selectedUser && (
        <div className="fixed inset-0 bg-text-primary bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${theme.card.background} rounded-lg p-8 max-w-md w-full mx-4`}>
            <h2 className={`text-2xl font-bold ${theme.text.primary} mb-1 flex items-center`}>
              <UserCheck className="w-6 h-6 mr-2 text-interactive-primary" />
              Assign DigiPART Username
            </h2>
            <p className={`${theme.text.secondary} text-sm mb-6`}>
              Assigning to: <strong>{selectedUser.full_name || selectedUser.username}</strong> ({selectedUser.email})
            </p>
            <form onSubmit={handleDigipartAssign}>
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                    DigiPART Username *
                  </label>
                  <input
                    type="text"
                    required
                    value={digipartUsername}
                    onChange={(e) => setDigipartUsername(e.target.value)}
                    placeholder="e.g. john.doe"
                    className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                    data-testid="digipart-username-input"
                  />
                </div>

                {/* 409 conflict warning */}
                {digipartConflict && (
                  <div className="p-3 rounded-lg bg-qc-warning/10 border border-qc-warning/30">
                    <p className="text-sm text-qc-warning">
                      This user already has DigiPART username <strong>"{digipartConflict}"</strong> assigned.
                      Enable "Force Overwrite" below to replace it. This will also clear their active session.
                    </p>
                    <label className="flex items-center mt-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={digipartForce}
                        onChange={(e) => setDigipartForce(e.target.checked)}
                        className="w-4 h-4 text-qc-warning border-border rounded focus:ring-qc-warning"
                        data-testid="digipart-force-checkbox"
                      />
                      <span className={`ml-2 text-sm ${theme.text.secondary}`}>Force overwrite existing assignment</span>
                    </label>
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowDigipartModal(false);
                    setSelectedUser(null);
                    setDigipartConflict(null);
                  }}
                  className={`px-4 py-2 ${theme.text.secondary} hover:bg-bg-subtle rounded-lg`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={digipartLoading || (!!digipartConflict && !digipartForce)}
                  className="px-4 py-2 bg-interactive-primary hover:bg-interactive-primary-hover disabled:opacity-50 text-text-inverse rounded-lg flex items-center space-x-2"
                  data-testid="digipart-assign-submit"
                >
                  {digipartLoading ? <Loader className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
                  <span>{digipartLoading ? 'Assigning...' : 'Assign'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
